import { useEffect, useMemo, useState } from 'react';
import { coverageCellKey, type RoleMapCoverage } from '@/api/console';
import type { RoleCoverageData } from './useRoleCoverageData';
import { computeAppCoverageRows, countByTab, matchesSearch, matchesTab } from './coverageHelpers';
import type { AppCoverageRow, CoverageTab, MatrixCell } from './types';

/** One request to move the editor's focus to a role row — a nonce (not just
 *  the role key) so clicking the SAME cell twice still re-triggers the
 *  scroll/focus effect in `RoleEditorCard`. */
export interface FocusRequest {
    roleKey: string;
    nonce: number;
}

/**
 * EAA-1817: the left-rail list + the matrix card's selection state — "which
 * app is open in the editor" and "which cell was last clicked" (so the
 * editor can scroll/focus that role's row per the design's "Nhấp một ô để mở
 * đúng app + vai trò đó").
 */
export function useCoverageBoard(data: RoleCoverageData) {
    const { coverageQuery, adminAppsQuery } = data;

    const [search, setSearch] = useState('');
    const [tab, setTab] = useState<CoverageTab>('all');
    const [selectedAppCode, setSelectedAppCode] = useState<string | null>(null);
    const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);

    const apps = useMemo(() => adminAppsQuery.data ?? [], [adminAppsQuery.data]);
    const coverage: RoleMapCoverage | undefined = coverageQuery.data;

    const appRows = useMemo<AppCoverageRow[]>(() => {
        if (!coverage) return [];
        return computeAppCoverageRows(apps, coverage);
    }, [apps, coverage]);

    // EAA-1817: pick a default selection once the rows resolve, so the
    // editor card is never blank on first load — mirrors the design frame,
    // which always shows one app already open. Never overrides a caller's
    // own click.
    useEffect(() => {
        if (selectedAppCode !== null) return;
        if (appRows.length === 0) return;
        setSelectedAppCode(appRows[0].code);
    }, [appRows, selectedAppCode]);

    const normalizedSearch = search.trim().toLowerCase();
    const visibleRows = useMemo(
        () => appRows.filter((row) => matchesSearch(row, normalizedSearch) && matchesTab(row, tab)),
        [appRows, normalizedSearch, tab]
    );

    const needsAttentionCount = useMemo(() => countByTab(appRows, 'needsAttention'), [appRows]);
    const uncoveredCount = useMemo(() => countByTab(appRows, 'uncovered'), [appRows]);

    const selectedAppRow = useMemo(
        () => appRows.find((row) => row.code === selectedAppCode) ?? null,
        [appRows, selectedAppCode]
    );

    /** Matrix rows: one per application, each holding one `MatrixCell` per
     *  `role_keys` column, in the coverage endpoint's own order. */
    const matrixRows = useMemo(() => {
        if (!coverage) return [];
        return apps.map((app) => ({
            app,
            cells: coverage.role_keys.map((roleKey): MatrixCell => {
                const cell = coverage.cells[coverageCellKey(app.code, roleKey)];
                return {
                    appCode: app.code,
                    roleKey,
                    state: cell?.state ?? 'none',
                    count: cell?.count ?? 0,
                    roles: cell?.roles ?? [],
                };
            }),
        }));
    }, [apps, coverage]);

    function selectCell(cell: MatrixCell) {
        setSelectedAppCode(cell.appCode);
        setFocusRequest((prev) => ({ roleKey: cell.roleKey, nonce: (prev?.nonce ?? 0) + 1 }));
    }

    function selectApp(appCode: string) {
        setSelectedAppCode(appCode);
    }

    return {
        search,
        setSearch,
        tab,
        setTab,
        appRows,
        visibleRows,
        needsAttentionCount,
        uncoveredCount,
        selectedAppCode,
        selectedAppRow,
        selectApp,
        matrixRows,
        roleKeys: coverage?.role_keys ?? [],
        focusRequest,
        selectCell,
    };
}

export type CoverageBoardVM = ReturnType<typeof useCoverageBoard>;
